export const isSameSenderMargin = (messages, m, i, userId) => {
  // console.log(i === messages.length - 1);

  if (
    i < messages.length - 1 &&
    messages[i + 1]?.sender?._id === m?.sender?._id &&
    messages[i]?.sender?._id !== userId
  )
    return 33;
  else if (
    (i < messages.length - 1 &&
      messages[i + 1]?.sender?._id !== m?.sender?._id &&
      messages[i]?.sender?._id !== userId) ||
    (i === messages.length - 1 && messages[i]?.sender?._id !== userId)
  )
    return 0;
  else return "auto";
};

export const isSameSender = (messages, m, i, userId) => {
  return (
    i < messages.length - 1 &&
    (messages[i + 1]?.sender?._id !== m?.sender?._id ||
      messages[i + 1]?.sender?._id === undefined) &&
    messages[i]?.sender?._id !== userId
  );
};

export const isLastMessage = (messages, i, userId) => {
  return (
    i === messages.length - 1 &&
    messages[messages.length - 1]?.sender?._id !== userId &&
    messages[messages.length - 1]?.sender?._id
  );
};

export const isSameUser = (messages, m, i) => {
  return i > 0 && messages[i - 1]?.sender?._id === m?.sender?._id;
};

export const getSender = (loggedUser, users) => {
  if (!Array.isArray(users) || users.length < 2) {
    return users?.[0]?.name || "Unknown";
  }
  if (!loggedUser?._id) {
    return users[0]?.name || "Unknown";
  }
  return users[0]?._id === loggedUser?._id
    ? users[1]?.name || "Unknown"
    : users[0]?.name || "Unknown";
};

export const getSenderFull = (loggedUser, users) => {
  if (!Array.isArray(users) || users.length === 0) {
    return null;
  }
  if (users.length < 2) {
    return users[0] || null;
  }
  if (!loggedUser?._id) {
    return users[0] || null;
  }
  return users[0]?._id === loggedUser?._id ? users[1] || null : users[0] || null;
};

export const normalizeRoomId = (room) => {
  if (!room) return null;
  const id = typeof room === "object" ? room._id || room.id : room;
  if (!id) return null;
  return id.toString();
};

// True only when an incoming typing event belongs to the currently open chat.
// A third user's typing in another chat must never trigger this conversation's bubble.
export const isTypingForChat = (roomId, chat) => {
  const normalized = normalizeRoomId(roomId);
  const chatId = chat?._id ? chat._id.toString() : null;
  return !!normalized && !!chatId && normalized === chatId;
};

const STORED_ENTITY_MAP = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#x27;": "'",
  "&#39;": "'",
};

// Messages stored before the escape-on-save bug was fixed contain HTML
// entities (e.g. "It&#x27;s urgent"). Decode them for display. New messages
// are stored raw, for which this is a no-op. Single pass only: it exactly
// inverts the single-pass escaping that was applied on save.
export const decodeStoredContent = (value) => {
  if (typeof value !== "string" || value.indexOf("&") === -1) return value;
  return value.replace(
    /&(amp|lt|gt|quot|#x27|#39);/g,
    (match) => STORED_ENTITY_MAP[match] || match
  );
};

// Sender id regardless of populated-object vs raw-id shape.
export const getMessageSenderId = (message) => {
  const sender = message?.sender;
  if (!sender) return null;
  if (typeof sender === "object") {
    return sender._id ? sender._id.toString() : null;
  }
  return sender.toString();
};

const getReadByIds = (message) => {
  if (!Array.isArray(message?.readBy)) return [];
  return message.readBy.map((id) => {
    if (id && typeof id === "object") {
      return id._id ? id._id.toString() : String(id);
    }
    return String(id);
  });
};

// Seen = every other chat member has this message in their readBy.
// (1:1 chats: exactly the other user. Groups: seen by all.)
// Only ever true for messages sent by me.
export const isMessageSeenByOthers = (message, chatUsers, myId) => {
  if (!message || !myId) return false;
  if (getMessageSenderId(message) !== myId.toString()) return false;
  const others = (Array.isArray(chatUsers) ? chatUsers : [])
    .map((u) => (u && typeof u === "object" ? u._id : u))
    .filter((id) => id && id.toString() !== myId.toString())
    .map((id) => id.toString());
  if (others.length === 0) return false;
  const readBy = getReadByIds(message);
  return others.every((id) => readBy.includes(id));
};

// Unread badge label, capped. Returns null when there is nothing to show.
export const formatUnreadCount = (count) => {
  const n = typeof count === "number" ? count : parseInt(count, 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 99 ? "99+" : String(n);
};

// "5m", "2h", "3d" style relative timestamps for notification rows.
export const formatTimeAgo = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  if (Number.isNaN(time)) return "";
  const diff = Date.now() - time;
  if (diff < 0) return "now";
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return "now";
  if (diff < hour) return `${Math.floor(diff / minute)}m`;
  if (diff < day) return `${Math.floor(diff / hour)}h`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d`;
  return date.toLocaleDateString();
};

// Per-message clock time, e.g. "14:32".
export const formatMessageTime = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const startOfDay = (d) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.getTime();
};

// Date divider labels: Today / Yesterday / "12 Feb" / "12 Feb 2024".
export const formatDateDivider = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const today = startOfDay(new Date());
  const day = startOfDay(date);
  const diffDays = Math.round((today - day) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};

export const isDifferentDay = (a, b) => {
  const da = a instanceof Date ? a : new Date(a);
  const db = b instanceof Date ? b : new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
  return startOfDay(da) !== startOfDay(db);
};

// "Online" / "last seen 5m ago" / "last seen 12 Feb".
export const formatLastSeen = (isOnline, lastSeen) => {
  if (isOnline) return "Online";
  if (!lastSeen) return "Offline";
  const date = lastSeen instanceof Date ? lastSeen : new Date(lastSeen);
  if (Number.isNaN(date.getTime())) return "Offline";
  const diff = Date.now() - date.getTime();
  if (diff < 60 * 1000) return "last seen just now";
  if (diff < 60 * 60 * 1000)
    return `last seen ${Math.floor(diff / (60 * 1000))}m ago`;
  if (diff < 24 * 60 * 60 * 1000)
    return `last seen ${Math.floor(diff / (60 * 60 * 1000))}h ago`;
  return `last seen ${date.toLocaleDateString([], {
    day: "numeric",
    month: "short",
  })}`;
};

// Group reaction arrays into {emoji, count, userIds, reactedByMe}.
export const groupReactions = (reactions, myId) => {
  const map = new Map();
  (Array.isArray(reactions) ? reactions : []).forEach((r) => {
    const emoji = r?.emoji;
    if (!emoji) return;
    const uid =
      r?.user && typeof r.user === "object"
        ? String(r.user._id || r.user)
        : String(r?.user || "");
    if (!map.has(emoji)) {
      map.set(emoji, { emoji, count: 0, userIds: [], reactedByMe: false });
    }
    const entry = map.get(emoji);
    entry.count += 1;
    if (uid) entry.userIds.push(uid);
    if (myId && uid === String(myId)) entry.reactedByMe = true;
  });
  return [...map.values()];
};
